import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { deploymentUsersApi, parametersApi, layersApi } from '../api/deploymentSettings.api';

// ── Query keys ────────────────────────────────────────────────────────────────
const keys = {
  users: (depId) => ['deployment', depId, 'users'],
  parameters: (depId) => ['deployment', depId, 'parameters'],
  parametersLive: (depId) => ['deployment', depId, 'parameters', 'live'],
  parametersStats: (depId, from, to) => ['deployment', depId, 'parameters', 'stats', from, to],
  parameterCharts: (depId, paramId, from, to, chartType) => ['deployment', depId, 'parameters', paramId, 'charts', from, to, chartType],
  layers: (depId) => ['deployment', depId, 'layers'],
};

// ── Deployment Users ──────────────────────────────────────────────────────────
export function useDeploymentUsers(depId) {
  return useQuery({
    queryKey: keys.users(depId),
    queryFn: () => deploymentUsersApi.list(depId),
    enabled: !!depId,
    staleTime: 30_000,
  });
}

export function useCreateDeploymentUser(depId) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload) => deploymentUsersApi.create(depId, payload),
    onSuccess: () => qc.invalidateQueries({ queryKey: keys.users(depId) }),
  });
}

export function useBulkCreateDeploymentUsers(depId) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (users) => deploymentUsersApi.bulkCreate(depId, users),
    onSuccess: () => qc.invalidateQueries({ queryKey: keys.users(depId) }),
  });
}

export function useUpdateDeploymentUser(depId) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ userId, payload }) => deploymentUsersApi.update(depId, userId, payload),
    onSuccess: () => qc.invalidateQueries({ queryKey: keys.users(depId) }),
  });
}

export function useDeleteDeploymentUser(depId) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (userId) => deploymentUsersApi.delete(depId, userId),
    onSuccess: () => qc.invalidateQueries({ queryKey: keys.users(depId) }),
  });
}

// ── Parameters ────────────────────────────────────────────────────────────────
export function useParameters(depId) {
  return useQuery({
    queryKey: keys.parameters(depId),
    queryFn: () => parametersApi.list(depId),
    enabled: !!depId,
    staleTime: 30_000,
  });
}

export function useParameterLive(depId) {
  return useQuery({
    queryKey: keys.parametersLive(depId),
    queryFn: () => parametersApi.live(depId),
    enabled: !!depId,
    staleTime: 60_000,
    refetchInterval: 60_000,
  });
}

export function useParameterStats(depId, from, to) {
  return useQuery({
    queryKey: keys.parametersStats(depId, from, to),
    queryFn: () => parametersApi.stats(depId, from, to),
    enabled: !!depId && !!from && !!to,
    staleTime: 60_000,
  });
}

export function useParameterCharts(depId, paramId, from, to, chartType) {
  return useQuery({
    queryKey: keys.parameterCharts(depId, paramId, from, to, chartType),
    queryFn: () => parametersApi.charts(depId, paramId, from, to, chartType),
    enabled: !!depId && !!paramId && !!from && !!to,
    staleTime: 60_000,
  });
}

export function useCreateParameter(depId) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload) => parametersApi.create(depId, payload),
    onSuccess: () => qc.invalidateQueries({ queryKey: keys.parameters(depId) }),
  });
}

export function useBulkCreateParameters(depId) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (file) => parametersApi.bulkCreate(depId, file),
    onSuccess: () => qc.invalidateQueries({ queryKey: keys.parameters(depId) }),
  });
}

export function useUpdateParameter(depId) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ paramId, payload }) => parametersApi.update(depId, paramId, payload),
    onSuccess: () => qc.invalidateQueries({ queryKey: keys.parameters(depId) }),
  });
}

export function useDeleteParameter(depId) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (paramId) => parametersApi.delete(depId, paramId),
    onSuccess: () => qc.invalidateQueries({ queryKey: keys.parameters(depId) }),
  });
}

// ── Layers ────────────────────────────────────────────────────────────────────
export function useLayers(depId) {
  return useQuery({
    queryKey: keys.layers(depId),
    queryFn: () => layersApi.list(depId),
    enabled: !!depId,
    staleTime: 30_000,
  });
}

export function useCreateLayer(depId) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload) => layersApi.create(depId, payload),
    onSuccess: () => qc.invalidateQueries({ queryKey: keys.layers(depId) }),
  });
}

export function useUpdateLayer(depId) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ layerId, payload }) => layersApi.update(depId, layerId, payload),
    onSuccess: () => qc.invalidateQueries({ queryKey: keys.layers(depId) }),
  });
}

export function useDeleteLayer(depId) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (layerId) => layersApi.delete(depId, layerId),
    onSuccess: () => qc.invalidateQueries({ queryKey: keys.layers(depId) }),
  });
}

export function useBulkCreateLayerMapping(depId) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (file) => layersApi.bulkCreateMapping(depId, file),
    onSuccess: () => qc.invalidateQueries({ queryKey: keys.layers(depId) }),
  });
}
